import firebase, { auth, db } from '../../../utils/firebase'

export const getUpdatedStoredUsersList = (userId, users, action) => {
    const userToUpdateIndex = users.findIndex(
        (userToUpdate) => userToUpdate.id === userId
    )

    if (userToUpdateIndex !== -1) {
        const usersList = users
        usersList[userToUpdateIndex].data = {
            ...usersList[userToUpdateIndex].data,
            ...action,
        }

        return usersList
    }

    return []
}

export const getInitialUsersFromDb = (
    limit,
    setUsers,
    setLastUser,
    setShouldLoadNextUsers,
    fetchingState,
    setFetchingState,
    errors,
    setErrors
) => {
    setFetchingState({ ...fetchingState, users: true })

    return db
        .collection('users')
        .limit(limit)
        .get()
        .then((snapshot) => {
            const users = getUsersData(snapshot)

            if (snapshot.docs && snapshot.docs.length >= 1) {
                setLastUser(snapshot.docs[snapshot.docs.length - 1])
            }

            setShouldLoadNextUsers(true)
            if (snapshot.docs && snapshot.docs.length < limit) {
                setShouldLoadNextUsers(false)
            }

            return setUsers(users)
        })
        .catch((error) => setErrors({ ...errors, users: error }))
        .finally(() => setFetchingState({ ...fetchingState, users: false }))
}

export const getNextUsersFromDb = (
    limit,
    setShouldLoadNextUsers,
    users,
    setUsers,
    lastUser,
    setLastUser,
    setIsNextUsersListLoading,
    errors,
    setErrors
) => {
    setIsNextUsersListLoading(true)

    return db
        .collection('users')
        .startAfter(lastUser)
        .limit(limit)
        .get()
        .then((snapshot) => {
            const newUsers = getUsersData(snapshot)

            if (snapshot.docs && snapshot.docs.length >= 1) {
                setLastUser(snapshot.docs[snapshot.docs.length - 1])
            }

            if (snapshot.docs && snapshot.docs.length < limit) {
                setShouldLoadNextUsers(false)
            }

            return setUsers([...users, ...newUsers])
        })
        .catch((error) => setErrors({ ...errors, nextUsers: error }))
        .finally(() => setIsNextUsersListLoading(false))
}

export const getCurrentAmountOfUsersFromDb = (
    currentUsersListCount,
    setShouldLoadNextUsers,
    setUsers,
    lastUser,
    setLastUser,
    errors,
    setErrors
) => {
    return db
        .collection('users')
        .limit(currentUsersListCount)
        .get()
        .then((snapshot) => {
            const users = getUsersData(snapshot)

            if (snapshot.docs && snapshot.docs.length >= 1) {
                setLastUser(snapshot.docs[snapshot.docs.length - 1])
            }

            setShouldLoadNextUsers(true)

            return setUsers(users)
        })
        .catch((error) => setErrors({ ...errors, users: error }))
}

export const disableUserAtList = async (
    user,
    users,
    setUsers,
    fetchingState,
    setFetchingState
) => {
    setFetchingState({ ...fetchingState, updateUser: true })
    let disableUserCloudFunc

    try {
        disableUserCloudFunc = firebase.functions().httpsCallable('disableUser')
        const result = await disableUserCloudFunc({ uid: user.id })

        const usersListToUpdate = getUpdatedStoredUsersList(user.id, users, {
            isActive: false,
        })
        if (usersListToUpdate.length) {
            setUsers(usersListToUpdate)
        }

        setFetchingState({ ...fetchingState, updateUser: false })
        return result.data
    } catch (err) {
        setFetchingState({ ...fetchingState, updateUser: false })
        return {
            error: true,
            message: 'Произошла ошибка при попытке деактивации пользователя',
        }
    }
}

export const restoreUserAtList = async (
    user,
    users,
    setUsers,
    fetchingState,
    setFetchingState
) => {
    setFetchingState({ ...fetchingState, updateUser: true })
    let activateUserCloudFunc

    try {
        activateUserCloudFunc = firebase
            .functions()
            .httpsCallable('activateUser')
        const result = await activateUserCloudFunc({ uid: user.id })

        const usersListToUpdate = getUpdatedStoredUsersList(user.id, users, {
            isActive: true,
        })
        if (usersListToUpdate.length) {
            setUsers(usersListToUpdate)
        }

        setFetchingState({ ...fetchingState, users: false })
        return result.data
    } catch (err) {
        setFetchingState({ ...fetchingState, users: false })
        return {
            error: true,
            message: 'Произошла ошибка при попытке активации пользователя',
        }
    }
}

export const createUserAtDb = async (
    user,
    users,
    setShouldLoadNextUsers,
    setUsers,
    lastUser,
    setLastUser,
    fetchingState,
    setFetchingState,
    errors,
    setErrors
) => {
    setFetchingState({ ...fetchingState, updateUser: true })
    const addUser = firebase.functions().httpsCallable('addUser')

    try {
        const result = await addUser({ user })
        await getCurrentAmountOfUsersFromDb(
            users.length,
            setShouldLoadNextUsers,
            setUsers,
            lastUser,
            setLastUser,
            fetchingState,
            setFetchingState,
            errors,
            setErrors
        )

        setFetchingState({ ...fetchingState, updateUser: false })
        return result.data
    } catch (err) {
        setFetchingState({ ...fetchingState, updateUser: false })
        return {
            error: true,
            message: 'Произошла ошибка, возможно проблемы с сервером',
        }
    }
}

export const resetPasswordRequest = (
    email,
    fetchingState,
    setFetchingState
) => {
    setFetchingState({ ...fetchingState, updateUser: true })

    return auth()
        .sendPasswordResetEmail(email)
        .then(() => {
            return { error: false, message: 'Сообщение было отправлено' }
        })
        .catch(() => {
            return { error: false, message: 'Ошибка при отправлении' }
        })
        .finally(() => {
            setFetchingState({ ...fetchingState, updateUser: false })
        })
}

export const updateCurrentUser = async (
    userId,
    updatedUser,
    users,
    setUsers,
    fetchingState,
    setFetchingState
) => {
    setFetchingState({ ...fetchingState, updateUser: true })
    const _updatedUser = firebase.functions().httpsCallable('updateUser')

    try {
        const result = await _updatedUser({ uid: userId, updatedUser })

        const usersListToUpdate = getUpdatedStoredUsersList(
            userId,
            users,
            updatedUser
        )
        if (usersListToUpdate.length) {
            setUsers(usersListToUpdate)
        }

        setFetchingState({ ...fetchingState, updateUser: false })
        return result.data
    } catch (error) {
        setFetchingState({ ...fetchingState, updateUser: false })
        return {
            error: true,
            message: 'Произошла ошибка, возможно проблемы с сервером',
        }
    }
}

export const searchInitialUsersAtDb = (
    limit,
    queryText,
    setUsers,
    setLastUser,
    setShouldLoadNextUsers,
    fetchingState,
    setFetchingState,
    errors,
    setErrors
) => {
    setFetchingState({ ...fetchingState, users: true })

    return db
        .collection('users')
        .limit(limit)
        .where('represent', '>=', queryText)
        .where('represent', '<=', queryText + '\uf8ff')
        .get()
        .then((snapshot) => {
            const users = getUsersData(snapshot)

            if (snapshot.docs && snapshot.docs.length >= 1) {
                setLastUser(snapshot.docs[snapshot.docs.length - 1])
            }

            setShouldLoadNextUsers(true)
            if (snapshot.docs && snapshot.docs.length < limit) {
                setShouldLoadNextUsers(false)
            }

            return setUsers(users)
        })
        .catch((error) => setErrors({ ...errors, users: error }))
        .finally(() => setFetchingState({ ...fetchingState, users: false }))
}

export const updateUserRepresent = (fetchingState, setFetchingState) => {
    setFetchingState({ ...fetchingState, users: true })
    db.collection('users')
        .get()
        .then((snapshot) =>
            snapshot.forEach((document) => {
                const docData = document.data()
                const name = docData.name || ''

                if (!docData.represent)
                    document.ref.update({ represent: name.toLowerCase() })
            })
        )
        .finally(() => setFetchingState({ ...fetchingState, users: false }))
}

export const searchNextUsersFromDb = (
    limit,
    queryText,
    setShouldLoadNextUsers,
    users,
    setUsers,
    lastUser,
    setLastUser,
    setIsNextUsersListLoading,
    errors,
    setErrors
) => {
    setIsNextUsersListLoading(true)

    return db
        .collection('users')
        .limit(limit)
        .where('represent', '>=', queryText)
        .where('represent', '<=', queryText + '\uf8ff')
        .startAfter(lastUser)
        .get()
        .then((snapshot) => {
            const newUsers = getUsersData(snapshot)

            if (snapshot.docs && snapshot.docs.length >= 1) {
                setLastUser(snapshot.docs[snapshot.docs.length - 1])
            }

            if (snapshot.docs && snapshot.docs.length < limit) {
                setShouldLoadNextUsers(false)
            }

            return setUsers([...users, ...newUsers])
        })
        .catch((error) => setErrors({ ...errors, nextUsers: error }))
        .finally(() => setIsNextUsersListLoading(false))
}

const getUsersData = (snapshot) => {
    const users = []
    snapshot.forEach((doc) => {
        const data = doc.data()
        users.push({
            id: doc.id,
            data: {
                ...data,
                displayName: `${data.name || ''} ${data.surname || ''}`,
                phone: data.phone || '',
                email: data.email || '',
            },
        })
    })

    return users
}
